import { ActionTree, MutationTree } from 'vuex';
import { AxiosResponse } from 'axios';
import { RootState } from '@/interfaces/stores/RootState';
import { Page } from '@/interfaces/api/Page';
import { Pagination } from '@/interfaces/api/Pagination';
import ComoManagedGiftApiService from '@/api/http/ComoManagedGiftApiService';
import { ComoGift, ComoManagedGift } from '@/interfaces/models/ComoManagedGift';

export interface ComoManagedGiftFilter {
  giftType?: string;
  status?: string;
  templateKey?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface ComoManagedGiftSavePayload {
  id?: string;
  data: Partial<ComoManagedGift>;
  image?: File;
}

interface ComoManagedGiftsState {
  hydratedGifts: ComoGift[];
  managedGifts: ComoManagedGift[];
  total: number;
  page: number;
  limit: number;
  filter: ComoManagedGiftFilter;
}

const api = new ComoManagedGiftApiService();

const state: ComoManagedGiftsState = {
  hydratedGifts: [],
  managedGifts: [],
  total: 0,
  page: 1,
  limit: 10,
  filter: {},
};

const actions: ActionTree<ComoManagedGiftsState, RootState> = {
  async fetchAvailableGifts({ commit }, venueId: string) {
    const [giftsResponse, managedResponse] = await Promise.all([
      api.gifts(venueId),
      api.paginate({ page: 1, limit: 100 }),
    ]);
    const managedGifts = [...(managedResponse.data.data || [])];
    const pageCount = Math.ceil(managedResponse.data.total / 100);

    for (let page = 2; page <= pageCount; page += 1) {
      const response = await api.paginate({ page, limit: 100 });
      managedGifts.push(...(response.data.data || []));
    }

    const managedByKey = new Map<string, ComoManagedGift>();

    managedGifts.forEach((gift) => {
      if (gift.templateKey) {
        managedByKey.set(gift.templateKey, gift);
      }
      if (gift.giftId) {
        managedByKey.set(gift.giftId, gift);
      }
    });

    commit(
      'setHydratedGifts',
      (giftsResponse.data || []).map((gift: ComoGift) => ({
        ...gift,
        additionalData: managedByKey.get(gift.templateKey) || managedByKey.get(gift.key),
      })),
    );
  },
  fetchManaged({ commit, state }, payload?: { page?: Page; filter?: ComoManagedGiftFilter }) {
    const page = payload?.page || { page: state.page, limit: state.limit };
    const filter = payload?.filter || state.filter;

    return api.paginate(page, filter).then((response: AxiosResponse<Pagination<ComoManagedGift>>) => {
      commit('setManagedGifts', response.data);
      commit('setPage', { page: page.page, limit: page.limit || state.limit });
      commit('setFilter', filter);
    });
  },
  save({ dispatch }, payload: ComoManagedGiftSavePayload) {
    const request = payload.id ? api.updateManagedGift(payload.id, payload.data) : api.create(payload.data);

    return request.then(async (response: AxiosResponse<ComoManagedGift>) => {
      if (payload.image && response.data._id) {
        const formData = new FormData();
        formData.set('asset', payload.image);
        await api.uploadImage(response.data._id, formData);
      }
      await dispatch('fetchManaged');
    });
  },
  delete({ dispatch }, id: string) {
    return api.delete(id).then(() => dispatch('fetchManaged'));
  },
  reset({ commit }) {
    commit('setHydratedGifts', []);
    commit('setManagedGifts', { data: [], total: 0 });
    commit('setPage', { page: 1, limit: 10 });
    commit('setFilter', {});
  },
};

const mutations: MutationTree<ComoManagedGiftsState> = {
  setHydratedGifts(state, gifts: ComoGift[]) {
    state.hydratedGifts = gifts;
  },
  setManagedGifts(state, pagination: Pagination<ComoManagedGift>) {
    state.managedGifts = pagination.data || [];
    state.total = pagination.total || 0;
  },
  setPage(state, page: { page: number; limit: number }) {
    state.page = page.page;
    state.limit = page.limit;
  },
  setFilter(state, filter: ComoManagedGiftFilter) {
    state.filter = filter;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
